import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../axiosBaseQuery';
import robsApiParser, { type Rob as MockRob, type RobProduct } from './robsApiParser';
import { MetaOption } from '../../../types';

// ---------- Types ----------
export interface Rob {
  id: string | number;
  srno: number;
  vessel: string;
  manager: string;
  port: string;
  date: string;
  type: string;
  remarks: string;
}

export interface RobsListResponse {
  data: Rob[];
  pagination: { totalElements: number };
}

interface CreateRobPayload {
  vesselId: number;
  managerId: number;
  eventDate?: Date | string | null;
  type: string;
  comments: string;
  eventDateValidForType: boolean;
  robProducts: any[];
}

export interface GetRobsListParams {
  [key: string]: any; // refine based on actual request params
}

// ---------- API Slice (merged) ----------
const robsApi = createApi({
  reducerPath: 'robsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Rob', 'Meta', 'Products', 'LubricationChart'],
  endpoints: (builder) => ({
    // Common ping URL to satisfy axiosBaseQuery; response is ignored by transformers
    // and replaced with local mock data until real endpoints are ready.

    // Grid list using axiosBaseQuery + transformResponse parser
    getRobsList: builder.mutation<RobsListResponse, GetRobsListParams>({
      query: (params) => {
        return {
          url: '/robs',
          method: 'GET',
          params: params,
        };
      },
      transformResponse: (res: any): RobsListResponse => robsApiParser.RobsListParser(res),
    }),

    // Products
    getProductsByVessel: builder.query<RobProduct[], { vesselId: number }>({
      query: ({ vesselId }: { vesselId?: number }) => ({
        url: `/vessels/${vesselId}/products`,
        method: 'GET',
      }),
      transformResponse: (res: any): RobProduct[] => robsApiParser.ProductsByVesselParser(res),
      providesTags: ['Products'],
    }),

    getUpdatedProductByVessel: builder.query({
      query: ({ vesselId, robId }: { vesselId: number; robId: number }) => ({
        url: '/robs/products',
        method: 'GET',
        params: { vesselId, robId },
      }),
      transformResponse: (res: any): RobProduct[] =>
        robsApiParser.UpdatedProductsByVesselParser(res),
    }),

    // Mutations for rob CRUD (mock)
    createRob: builder.mutation<MockRob, CreateRobPayload>({
      query: (payload) => ({
        url: '/robs',
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['Rob'],
    }),

    updateRob: builder.mutation<MockRob, { id: string; payload: CreateRobPayload }>({
      query: ({ id, payload }: { id: string; payload: CreateRobPayload }) => ({
        url: `/robs/${id}`,
        method: 'PUT',
        data: { robId: id, ...payload },
      }),
    }),

    deleteRob: builder.mutation<any, { id: number; vesselId: number }>({
      query: ({ id, vesselId }) => ({
        url: `/robs/${id}/${vesselId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Rob'],
    }),

    getAllManagers: builder.query<MetaOption[], void>({
      query: () => ({
        url: '/manager',
        method: 'GET',
      }),
      transformResponse: (res: any): MetaOption[] => robsApiParser.managersParser(res),
    }),

    getVesselByManagerId: builder.query<MetaOption[], number>({
      query: (managerId: number | null) => ({
        url: `/vessels/manager/${managerId}`,
        method: 'GET',
      }),
      transformResponse: (res: any): MetaOption[] => robsApiParser.vesselsByManagerParser(res),
    }),

    getAvailableDatesForRob: builder.query<
      string[],
      { managerId: number | null; vesselId: number | null }
    >({
      query: ({ managerId, vesselId }: { managerId: number | null; vesselId: number | null }) => ({
        url: `/management-periods?managerId=${managerId}&vesselId=${vesselId}&active=true`,
        method: 'GET',
      }),
      transformResponse: (res: any): string[] => robsApiParser.availableDatesParser(res),
    }),

    getVesselFilterOptions: builder.query<MetaOption[], void>({
      query: () => ({
        url: '/robs/vessels',
        method: 'GET',
      }),
      transformResponse: (res: any): MetaOption[] => robsApiParser.vesselsFilterOptionsParser(res),
    }),

    getManagerFilterOptions: builder.query<MetaOption[], void>({
      query: () => ({
        url: '/robs/managers',
        method: 'GET',
      }),
      transformResponse: (res: any): MetaOption[] => robsApiParser.managersFilterOptionsParser(res),
    }),

    getTypeFilterOptions: builder.query<string[], void>({
      query: () => ({
        url: '/robs/types',
        method: 'GET',
      }),
      transformResponse: (res: any): string[] => robsApiParser.typesFilterOptionsParser(res),
    }),
  }),
});

// ---------- Reducer, Middleware, Path ----------
export const robsReducer = robsApi.reducer;
export const robsMiddleware = robsApi.middleware;
export const robsReducerPath = robsApi.reducerPath;

// ---------- Hooks / Actions ----------
export const {
  useGetRobsListMutation,
  useGetProductsByVesselQuery,
  useCreateRobMutation,
  useUpdateRobMutation,
  useDeleteRobMutation,
  useGetAllManagersQuery,
  useGetUpdatedProductByVesselQuery,
  useGetVesselByManagerIdQuery,
  useGetAvailableDatesForRobQuery,
  useGetVesselFilterOptionsQuery,
  useGetManagerFilterOptionsQuery,
  useGetTypeFilterOptionsQuery,
} = robsApi;
